import { call, put, takeLatest, select } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchFacebookPagesRequest,
  fetchFacebookPagesSuccess,
  fetchFacebookPagesFailure,
  updatePageRequest,
  updatePageSuccess,
  updatePageFailure,
} from '../slices/facebookPagesSlice';
import { fetchIntegrationsRequest } from '../slices/integrationsSlice';
import type { FacebookPage } from '../slices/facebookPagesSlice';
import { ENDPOINTS } from '../../api/endPoints';
import apiService from '../../api/service/apiService';
import type { RootState } from '../store';

// GET Facebook/Instagram pages API call
function* fetchFacebookPages(action: PayloadAction<{ integrationId: string }>) {
  try {
    const { integrationId } = action.payload;
    
    // Get restaurant ID from state
    const restaurantId: string = yield select((state: RootState) => state.clover.restaurantId);
    
    if (!restaurantId) {
      throw new Error('Restaurant ID not found');
    }

    // Get the platform from integrations state to determine which endpoint to use
    const integrations: any[] = yield select((state: RootState) => state.integrations.data);
    const integration = integrations.find(int => int.id === integrationId);
    const platform = integration?.provider?.toLowerCase() || 'facebook';

    // Use new dynamic endpoint structure
    const endpoint = ENDPOINTS.integrations.fetchPages(platform as any, integrationId);
    
    if (!endpoint) {
      throw new Error(`Pages endpoint not configured for ${platform}`);
    }
    
    const response: Response = yield call(fetch, endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'restaurant-id': restaurantId,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch pages: ${response.statusText}`);
    }

    const data: {
      success: boolean;
      message: string;
      data: {
        data: FacebookPage[];
      };
    } = yield response.json();

    let pages: FacebookPage[] = [];
    
    if (data.success && data.data?.data) {
      // Extract pages from nested data structure
      pages = data.data.data;
    } else if (data.data) {
      // Handle case where pages are directly in data
      if (Array.isArray(data.data)) {
        pages = data.data;
      } else if (typeof data.data === 'object') {
        pages = [data.data as any];
      }
    }
    
    if (pages.length > 0) {
      yield put(fetchFacebookPagesSuccess(pages));
    } else {
      throw new Error('No pages found in response');
    }
  } catch (error: any) {
    console.error('Error fetching pages:', error);
    yield put(fetchFacebookPagesFailure(error.message || 'Failed to fetch pages'));
  }
}

// PUT Update selected page
function* updatePage(action: PayloadAction<{ integrationId: string; pageId: string; platform: string }>) {
  try {
    const { integrationId, pageId, platform } = action.payload;
    
    // Get restaurant ID from state
    const restaurantId: string = yield select((state: RootState) => state.clover.restaurantId);
    
    if (!restaurantId) {
      throw new Error('Restaurant ID not found');
    }

    // Use dynamic endpoint structure for update
    const endpoint = ENDPOINTS.integrations.update(platform as any, integrationId);
    
    if (!endpoint) {
      throw new Error(`Update endpoint not configured for ${platform}`);
    }
    
    // @ts-ignore
    const response: any = yield call(apiService.put, {
      endpoint,
      body: {
        pageId,
      },
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    // Extract page data from configuration.pages array in response
    const integrationData = response.data?.data || response.data;
    const pageData = integrationData?.configuration?.pages?.[0];

    if (pageData) {
      yield put(updatePageSuccess(pageData));
      
      // Refetch integrations to update configuration.pages in the integrations state
      console.log('[updatePage] Page updated successfully, refetching integrations...');
      yield put(fetchIntegrationsRequest({ restaurantId }));
    } else {
      throw new Error('No page data in response');
    }
  } catch (error: any) {
    console.error('Error updating page:', error);
    yield put(updatePageFailure(error.message || 'Failed to update page'));
  }
}

// Root saga
export default function* facebookPagesSaga() {
  yield takeLatest(fetchFacebookPagesRequest.type, fetchFacebookPages);
  yield takeLatest(updatePageRequest.type, updatePage);
}
